import { targetsAvailableForKind } from './scheduleWatchAgents';

import type {
  WatchFinding,
  WatchRun,
  WatchSchedule,
  WatchTargetKind,
  WatchTowerMockState,
} from './types';

const ELEVATED_SEVERITIES = new Set(['high', 'critical']);

export interface WatchTick {
  scheduleId: number,
  scheduledAt: string,
  type: string,
  status: string,
  targetCount: number,
  elevatedCount: number,
  costUsd: number,
  runIds: number[],
}

export interface WatchScheduleRunSummary {
  schedule: WatchSchedule,
  tickCount: number,
  lastTickAt: string | null,
  lastStatus: string | null,
}

export function tickKey(scheduleId: number, scheduledAt: string): string {
  return `${scheduleId}|${scheduledAt}`;
}

export function isElevatedSeverity(severity: string): boolean {
  return ELEVATED_SEVERITIES.has(severity);
}

export function findingsForRun(state: WatchTowerMockState, runId: number): WatchFinding[] {
  return state.findings.filter((finding) => finding.agentRunId === runId);
}

export function elevatedFindingCount(findings: WatchFinding[]): number {
  return findings.filter((finding) => isElevatedSeverity(finding.severity)).length;
}

export function rollupRunStatus(statuses: string[]): string {
  if (!statuses.length) {
    return 'none';
  }
  if (statuses.some((status) => status === 'failed')) {
    return 'failed';
  }
  if (statuses.some((status) => status === 'running' || status === 'in_progress')) {
    return 'running';
  }
  if (statuses.every((status) => status === 'completed')) {
    return 'completed';
  }
  return 'partial';
}

export function formatIsoDateTime(value: string | null): string {
  if (!value) {
    return '—';
  }
  return value.replace('T', ' ').replace('.000Z', ' UTC');
}

export function formatDurationMs(ms: number | null): string {
  if (ms == null) {
    return '—';
  }
  if (ms < 1000) {
    return `${ms} ms`;
  }
  return `${Math.round(ms / 1000)} s`;
}

export function formatCostUsd(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return `$${value.toFixed(2)}`;
}

export function formatConfidence(value: number | null): string {
  if (value == null) {
    return '—';
  }
  return value.toFixed(2);
}

export function tokenCountLabel(run: WatchRun): string {
  const value = run.summary?.tokens ?? run.summary?.total_tokens;
  if (value == null) {
    return '—';
  }
  return String(value);
}

export function payloadPreview(payload: Record<string, unknown> | null): string {
  if (!payload) {
    return '—';
  }
  const keys = Object.keys(payload);
  if (!keys.length) {
    return '—';
  }
  const first = keys[0];
  const raw = payload[first];
  const rendered = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
  const text = `${first}: ${rendered}`;
  if (text.length > 80) {
    return `${text.slice(0, 77)}...`;
  }
  return text;
}

export function payloadEntries(
  payload: Record<string, unknown> | null,
): { key: string, value: string }[] {
  if (!payload) {
    return [];
  }
  return Object.entries(payload).map(([key, value]) => ({
    key,
    value: typeof value === 'object' ? JSON.stringify(value) : String(value),
  }));
}

export function resolveTargetName(
  state: WatchTowerMockState,
  companyId: number | null,
  kind: string,
  targetId: number,
): string {
  if (kind === 'topic') {
    const topic = (state.watchTopics || []).find((item) => item.id === targetId);
    return topic?.title || String(targetId);
  }
  const company = state.watchCompanies.find((item) => item.id === companyId);
  if (!company) {
    return String(targetId);
  }
  const items = targetsAvailableForKind(company, kind as WatchTargetKind);
  return items.find((item) => item.id === targetId)?.label || String(targetId);
}

export function groupTicks(state: WatchTowerMockState): WatchTick[] {
  const buckets = new Map<string, WatchRun[]>();
  state.runs.forEach((run) => {
    const key = tickKey(run.scheduleId, run.scheduledAt);
    const current = buckets.get(key) || [];
    buckets.set(key, [...current, run]);
  });

  return Array.from(buckets.values())
    .map((runs) => {
      const first = runs[0];
      const findings = runs.flatMap((run) => findingsForRun(state, run.id));
      const cost = runs.reduce((sum, run) => sum + (run.costUsd || 0), 0);
      return {
        scheduleId: first.scheduleId,
        scheduledAt: first.scheduledAt,
        type: first.type,
        status: rollupRunStatus(runs.map((run) => run.status)),
        targetCount: runs.length,
        elevatedCount: elevatedFindingCount(findings),
        costUsd: cost,
        runIds: runs.map((run) => run.id),
      };
    })
    .sort((left, right) => right.scheduledAt.localeCompare(left.scheduledAt));
}

export function ticksForSchedule(state: WatchTowerMockState, scheduleId: number): WatchTick[] {
  return groupTicks(state).filter((tick) => tick.scheduleId === scheduleId);
}

export function runsForTick(
  state: WatchTowerMockState,
  scheduleId: number,
  scheduledAt: string,
): WatchRun[] {
  return state.runs.filter((run) => (
    run.scheduleId === scheduleId && run.scheduledAt === scheduledAt
  ));
}

export function scheduleRunSummaries(
  state: WatchTowerMockState,
  agentType = '',
): WatchScheduleRunSummary[] {
  const ticks = groupTicks(state);
  return state.schedules
    .filter((schedule) => !agentType || schedule.type === agentType)
    .map((schedule) => {
      const ownTicks = ticks.filter((tick) => tick.scheduleId === schedule.id);
      const last = ownTicks[0] || null;
      return {
        schedule,
        tickCount: ownTicks.length,
        lastTickAt: last?.scheduledAt || null,
        lastStatus: last?.status || null,
      };
    });
}
